// Copyright 2025, University of Maryland, All Rights Reserved
import { apiClient } from './api-client';
import type {
  ConnectorRecord,
  CacheRecord,
  CreateCacheRequest,
  UpdateCacheRequest,
  RefreshResponse,
  SubscriptionRecord,
  CreateSubscriptionRequest,
  UpdateSubscriptionRequest,
} from './types/connectors';

/**
 * List all connectors with their nested caches.
 */
export async function listConnectors(): Promise<ConnectorRecord[]> {
  const response = await apiClient.get<ConnectorRecord[]>('/admin/connectors');
  return response.data;
}

/**
 * Add a cache to a connector.
 */
export async function createCache(
  connectorId: string,
  data: CreateCacheRequest,
): Promise<CacheRecord> {
  const response = await apiClient.post<CacheRecord>(
    `/admin/connectors/${encodeURIComponent(connectorId)}/caches`,
    data,
  );
  return response.data;
}

/**
 * Update a cache's schedule.
 */
export async function updateCache(
  connectorId: string,
  cacheId: string,
  data: UpdateCacheRequest,
): Promise<CacheRecord> {
  const response = await apiClient.put<CacheRecord>(
    `/admin/connectors/${encodeURIComponent(connectorId)}/caches/${encodeURIComponent(cacheId)}`,
    data,
  );
  return response.data;
}

/**
 * Delete a cache from a connector.
 */
export async function deleteCache(connectorId: string, cacheId: string): Promise<void> {
  await apiClient.delete(
    `/admin/connectors/${encodeURIComponent(connectorId)}/caches/${encodeURIComponent(cacheId)}`,
  );
}

/**
 * Trigger a cache refresh (queues a background job).
 */
export async function triggerRefresh(
  connectorId: string,
  cacheId: string,
  force = false,
): Promise<RefreshResponse> {
  const response = await apiClient.post<RefreshResponse>(
    `/admin/connectors/${encodeURIComponent(connectorId)}/caches/${encodeURIComponent(cacheId)}/refresh`,
    null,
    { params: { force } },
  );
  return response.data;
}

/**
 * List subscriptions for a connector cache.
 */
export async function listSubscriptions(
  connectorId: string,
  cacheId: string,
): Promise<SubscriptionRecord[]> {
  const response = await apiClient.get<SubscriptionRecord[]>(
    `/admin/connectors/${encodeURIComponent(connectorId)}/caches/${encodeURIComponent(cacheId)}/subscriptions`,
  );
  return response.data;
}

/**
 * Subscribe a workspace to a connector cache.
 */
export async function createSubscription(
  connectorId: string,
  cacheId: string,
  data: CreateSubscriptionRequest,
): Promise<SubscriptionRecord> {
  const response = await apiClient.post<SubscriptionRecord>(
    `/admin/connectors/${encodeURIComponent(connectorId)}/caches/${encodeURIComponent(cacheId)}/subscriptions`,
    data,
  );
  return response.data;
}

/**
 * Update a subscription's pipeline.
 */
export async function updateSubscription(
  connectorId: string,
  cacheId: string,
  orgId: string,
  wsId: string,
  data: UpdateSubscriptionRequest,
): Promise<SubscriptionRecord> {
  const response = await apiClient.put<SubscriptionRecord>(
    `/admin/connectors/${encodeURIComponent(connectorId)}/caches/${encodeURIComponent(cacheId)}/subscriptions/${encodeURIComponent(orgId)}/${encodeURIComponent(wsId)}`,
    data,
  );
  return response.data;
}

/**
 * Unsubscribe a workspace from a connector cache.
 */
export async function deleteSubscription(
  connectorId: string,
  cacheId: string,
  orgId: string,
  wsId: string,
): Promise<void> {
  await apiClient.delete(
    `/admin/connectors/${encodeURIComponent(connectorId)}/caches/${encodeURIComponent(cacheId)}/subscriptions/${encodeURIComponent(orgId)}/${encodeURIComponent(wsId)}`,
  );
}
